import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { GET_SEARCH_FILTER_DEFINITIONS_QUERY as PUBLIC_GET_SEARCH_FILTER_DEFINITIONS_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationPublicRpc.dfc.new';
import { GET_SEARCH_FILTER_DEFINITIONS_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc.dfc.new';
import { useAppContext } from '../../context/AppContext';
import { usePersonalizationQuery } from '../../hooks/usePersonalizationQuery';
import { useProductTypeSearchParam } from '../../hooks/useProductTypeSearchParams';
import { getBaseFilterConfig } from '../utils/BaseFilterConfig';
/**
 * This hook does the following:
 *
 * 1. Combines the base filter config (in the FE source code) with the response from the filter
 *    config RPC, so that you don't have to worry about what's defined where for the filters
 *
 * 2. Excludes filters that are either not applicable for the current app context
 *    (some filters are in-app only, for example)
 *
 * 3. Excludes product-specific filters that do not match the current product type param, if it exists
 */
export function useFilterConfig() {
  const appContext = useAppContext();
  const productType = useProductTypeSearchParam();
  const {
    collectionSlug
  } = useParams();
  const {
    data
  } = usePersonalizationQuery({
    app: GET_SEARCH_FILTER_DEFINITIONS_QUERY,
    public: PUBLIC_GET_SEARCH_FILTER_DEFINITIONS_QUERY
  });
  return useMemo(() => {
    const baseConfig = getBaseFilterConfig();
    const completeConfig = {};
    Object.entries(baseConfig).forEach(([key, filter]) => {
      var _ref, _filter$options2, _ref2, _filter$optionGroups2;
      // Check if the filter is allowed for the current app context
      if (filter.allowedAppContext && filter.allowedAppContext !== appContext) return;

      // If a product type is specified, and it doesn't match the current product type, skip it
      if (filter.productType && productType && filter.productType !== productType) return;

      // INBOUND: hide product type filter for collectionsq
      if (collectionSlug && filter.field === 'PRODUCT_TYPE') return;

      // INBOUND: if this is a collection, hide filters that aren't app-specific or universal
      if (collectionSlug && filter.productType && filter.productType !== 'APP') {
        return;
      }
      const fieldWithEcoPrefix = `eco_${filter.field}`;

      // Falling back to empty arrays allows us to render the filter controls while loading.
      if (!data) {
        var _filter$options, _filter$optionGroups;
        completeConfig[key] = Object.assign({}, filter, {
          fieldWithEcoPrefix,
          options: (_filter$options = filter.options) !== null && _filter$options !== void 0 ? _filter$options : [],
          optionGroups: (_filter$optionGroups = filter.optionGroups) !== null && _filter$optionGroups !== void 0 ? _filter$optionGroups : []
        });
        return;
      }
      const definition = getFilterDefinition(filter, data.result);
      completeConfig[key] = Object.assign({}, filter, {
        fieldWithEcoPrefix,
        // If the base filter has hardcoded options, use them. Otherwise, use the options from the RPC.
        // The final `?? []` shouldn't happen, since if the filter definition is missing from the RPC,
        // then it should be hardcoded in the base filter.
        options: (_ref = (_filter$options2 = filter.options) !== null && _filter$options2 !== void 0 ? _filter$options2 : definition === null || definition === void 0 ? void 0 : definition.options) !== null && _ref !== void 0 ? _ref : [],
        optionGroups: (_ref2 = (_filter$optionGroups2 = filter.optionGroups) !== null && _filter$optionGroups2 !== void 0 ? _filter$optionGroups2 : definition === null || definition === void 0 ? void 0 : definition.optionGroups) !== null && _ref2 !== void 0 ? _ref2 : []
      });
    });
    return completeConfig;
  }, [data, appContext, productType, collectionSlug]);
}
function getFilterDefinition(filter, result) {
  const definitions = filter.productType ? result.filtersByProductType[filter.productType] : result.universalFilters;
  return definitions === null || definitions === void 0 ? void 0 : definitions.find(f => f.baseQueryField === filter.field);
}